import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface StatusCounts {
  all: number;
  active: number;
  pending: number;
  sold: number;
  expired: number;
  draft: number;
}

export const usePropertyStatusCounts = (searchTerm: string = '') => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['property-status-counts', user?.id, searchTerm],
    queryFn: async (): Promise<StatusCounts> => {
      if (!user?.id) return { all: 0, active: 0, pending: 0, sold: 0, expired: 0, draft: 0 };
      
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch all properties for counting (we need to count on client due to complex date logic)
      let query = supabase
        .from('properties')
        .select('status, closed_date, contract_expiration_date')
        .eq('seller_id', user.id);
      
      if (searchTerm) {
        query = query.or(`address.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching property counts:', error);
        throw error;
      }
      
      const properties = data || [];
      
      // Count by status
      const counts: StatusCounts = {
        all: properties.length,
        active: 0,
        pending: 0,
        sold: 0,
        expired: 0,
        draft: 0
      };
      
      properties.forEach(p => {
        // Draft: status is draft
        if (p.status === 'draft') {
          counts.draft++;
          return;
        }

        // Sold: has closed_date
        if (p.closed_date) {
          counts.sold++;
          return;
        }
        
        // Expired: no closed_date, has contract_expiration_date on or before today
        if (p.contract_expiration_date && p.contract_expiration_date <= today) {
          counts.expired++;
          return;
        }
        
        // Active or Pending: no closed_date, not expired
        if (p.status === 'active') {
          counts.active++;
        } else if (p.status === 'pending') {
          counts.pending++;
        }
      });
      
      return counts;
    },
    enabled: !!user?.id,
    staleTime: 30000 // Cache for 30 seconds to avoid excessive re-fetching
  });
};
